// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// import './MyAlerts.scss';
// Component imports:
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/pro-solid-svg-icons';
// Service imports:
import { formatDate } from '../../services/utils';
// Action imports:
import { setSelectedTick } from '../../redux-store/actions/dashboardActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { AlertTickData } from '../../types/api/AlertTypes';
import { AlertDataIdToCauseMap } from './SigtiRange';
import { sortTickData } from '../../services/sortUtils';
import AlertReviewModal from '../alerts/AlertReviewModal';

interface IOwnProps {
    alertDataIdToCauseMap: AlertDataIdToCauseMap
    symbol: string,
};
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps & IOwnProps;

interface IState {
    selectedAlert: AlertTickData | null
};

/**
 * This component displays My Alerts under alerts/Bookmarks tab **Found NavBar Alerts (Alerts/Bookmarks)**
 * - Subcomponent of SigtiRange
 */
class MyAlerts extends React.Component<Props, IState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedAlert: null
        };
    }

    displayModal = () => {
        const { selectedAlert } = this.state;
        if (selectedAlert !== null) {
            return (
                <div>
                    <AlertReviewModal
                        alert={selectedAlert}
                        onHide={() => this.setState({ selectedAlert: null })}
                    />
                </div>
            );
        }
        return null;
    }

    displayRows = () => {
        const { alertsByDate, currentTradingDay, symbol, setSelectedTick, selectedTick, alertDataIdToCauseMap } = this.props;
        const alertInfo = alertsByDate?.[currentTradingDay];

        if (alertInfo === undefined || alertInfo === 'waiting') {
            return (
                <tr className='NoClick'>
                    <td className='text-left' colSpan={4}>
                        Loading...
                    </td>
                </tr>
            );
        }

        if (alertInfo instanceof Error) {
            return (
                <tr className='NoClick'>
                    <td className='text-left' colSpan={4}>
                        Error: {alertInfo.message}
                    </td>
                </tr>
            );
        }

        const alertsBySymbol = Array.isArray(alertInfo) 
            ? alertInfo.filter(alert => alert.symbol === symbol) 
            : [];

        const sortedAlertsBySymbol = sortTickData(alertsBySymbol);
        
        const jsxRows = sortedAlertsBySymbol.map((alert) => {
            const alertDataId = alert.ad_id;
            const cause = (alertDataId)
                ? alertDataIdToCauseMap[alertDataId]
                : undefined;
            return (
                <tr
                    key={alert.ad_id}
                    className={(alert === selectedTick) ? 'current' : undefined}
                    onClick={() => setSelectedTick(alert)}
                >
                    <td>{formatDate(alert.ref_timestamp, true, true)}</td>
                    <td>
                        {(cause) && (
                            <Button
                                variant='outline-dark'
                                className='VerySmallButton CauseButton'
                                title='Go to Cause'
                                // @ts-ignore
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setSelectedTick(cause);
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faBell} color="#FFB31F"
                                />
                            </Button>
                        )}
                        {alert.description}
                    </td>
                    <td>
                        <div
                            className={`AlertStatusCircle StatusColorOnly ${alert.status || 'forreview'}`}
                        />
                    </td>
                    <td>
                        <Button
                            variant='dark'
                            className='VerySmallButton OpenAlertButton'
                            onClick={(e: React.MouseEvent) => { e.stopPropagation(); this.setState({ selectedAlert: alert })}}
                        >
                            Review
                        </Button>
                    </td>
                </tr>
            );
        });

        if (jsxRows.length === 0) {
            return (
                <tr className='NoClick'>
                    <td className='text-left EmptyTableText' colSpan={5}>No Alerts</td>
                </tr>
            )
        }

        return jsxRows;
    }

    render () {
        return (
            <div id='MyAlerts'>
                <h5 className='SectionTitle'>Alerts</h5>
                <Table className='CompressedTable ClickableRows'>
                    <thead>
                        <tr>
                            <th>Timestamp</th>
                            <th>Alert</th>
                            <th className='text-center'>Status</th>
                            <th className='text-center'>Review</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.displayRows()}
                    </tbody>
                </Table>
                {this.displayModal()}
            </div>
        );
    }
}

function mapStateToProps({ alertReducer, dashboardReducer }: RootState) {
    const { alertsByDate } = alertReducer;
    const { currentTradingDay, selectedTick } = dashboardReducer;
    return {
        alertsByDate,
        currentTradingDay,
        selectedTick
    };
}

const connector = connect(mapStateToProps, { setSelectedTick });
export default connector(MyAlerts);
